import React, { useState, useEffect } from 'react';
import dividia_logo_white from '../images/dividia_white_blue.png';
import dividia_logo_dark from '../images/dividia_black_blue.png';
import { getTheme } from '../util/theme';
import ControlBoxView from '../live/ControlBoxView';
import Sheet from './Sheet';

/**
 * MobileTopBar — shared mobile identity bar for Live and Playback views.
 *
 * Two-row layout (redesign #86bb5dx62):
 *   row 1:  logo · system name (ellipsis) ······ clock
 *   meta:   ·········· product badge · serial (center) ·········· user (right)
 *
 * Props:
 *   sName    — server / product name
 *   bSerial  — server serial number (hidden for cloud)
 *   fEview   — boolean, enterprise (green EV badge) vs local (blue DV badge)
 *   mode     — optional product mode ('dview'|'eview'|'cloud-modern'|'cloud-legacy');
 *              when cloud, shows a CLOUD badge, the product name, and no serial.
 *   nvrTime  — formatted clock time (local NVR clock; cloud shows user-local time)
 *   nvrDate  — formatted clock date (optional; shown above the time so the header carries both — #86bb5dx62)
 *   userName — logged-in user, shown right-aligned under the clock
 *   daysRec  — days of recorded footage (optional number; shown in the meta row's left cell — #86bb5dx62)
 *   onInfoClick — optional; when provided, the meta row's left cell shows a tappable ⓘ that opens the
 *                 System-details sheet (the mobile home for the desktop bottom-bar ⓘ). Live passes it;
 *                 Playback omits it (so no chip there).
 */
const MobileTopBar = ({ sName, bSerial, fEview, nvrTime, nvrDate, mode, userName, daysRec, onInfoClick,
                        onJump, onViews, viewLabel, viewDirty, canJump, usersOnline }) => {
  // TABLET+ identity crumbs (#86bb7phhh): the host (Live tablet) may wire onJump / onViews so the header
  // shows the current SYSTEM (Jump) and current VIEW (Views) as clickable crumbs — mirroring the desktop
  // header breadcrumb — instead of a redundant static name. CSS gates them to tablet+ under
  // .mobile-topbar--crumbs; mobile and not-yet-wired hosts keep the static name (locked mobile unchanged).
  const [theme, setTheme] = useState(getTheme);

  useEffect(() => {
    const onThemeChange = (e) => setTheme(e.detail);
    window.addEventListener('themechange', onThemeChange);
    return () => window.removeEventListener('themechange', onThemeChange);
  }, []);

  const isCloud = mode === 'cloud-modern' || mode === 'cloud-legacy';
  const badge = isCloud ? 'CLOUD' : (fEview ? 'EV' : 'DV');
  const showSerial = !isCloud && !!bSerial;
  const modeClass = isCloud ? 'mobile-topbar--cloud' : (fEview ? 'mobile-topbar--eview' : '');

  // Tablet+ account avatar (#86bb7phhh) — opens a BOTTOM SHEET (shared Sheet) to match every other tablet
  // menu (Views/Jump/ⓘ/Overlays), rather than a desktop-style dropdown (David 2026-08-07). Only rendered
  // when the host wires the crumbs (tablet Live), so mobile keeps its meta row + is untouched.
  const [acctOpen, setAcctOpen] = useState(false);
  const productTag = isCloud ? ' · Cloud' : (fEview ? ' · eView' : '');
  const acctInitial = ((userName || '?').trim().charAt(0) || '?').toUpperCase();
  // Ambient status readout under the clock (tablet+, #86bb7phhh) — serial · days rec · users online, all
  // already in redux at login (usersOnline comes down in the post-login payload). No new fetch needed.
  const onlineCount = Array.isArray(usersOnline) ? usersOnline.length
    : (typeof usersOnline === 'number' ? usersOnline : null);

  return (
    <div className={`mobile-topbar ${modeClass}${(onJump || onViews) ? ' mobile-topbar--crumbs' : ''}`}>
      <div className="mobile-topbar__row">
        <img
          src={theme === 'dark' ? dividia_logo_white : dividia_logo_dark}
          className="mobile-topbar__logo"
          alt="Dividia"
        />
        {/* Name + the System-details ⓘ (#86bb5dx62) — the ⓘ sits at the END of the system name
            (David: better there than under the logo). Tap → System-details sheet. */}
        <span className="mobile-topbar__namewrap">
          <span className="mobile-topbar__name">{sName || 'NVR'}</span>
          { onInfoClick &&
            <button type="button" className="mobile-topbar__info" onClick={onInfoClick} aria-label="System details">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" /></svg>
            </button> }
        </span>
        {/* Tablet+ identity crumbs — Jump (current system) / Views (current view), mirroring the desktop
            breadcrumb. Shown ≥768 via CSS (.mobile-topbar--crumbs); the static name above is hidden there.
            Jump crumb is gated on canJump (hasJump) so eView/cloud follow that capability. #86bb7phhh */}
        {(onJump || onViews) && (
          <div className="mobile-topbar__crumbs">
            {/* Jump crumb — rendered whenever the product supports jump (canJump). Live wires onJump so it's
                a live control; Playback (until playback-Jump #4) leaves it an INVISIBLE reserved spacer, so
                the crumbs zone is the same width on both screens and the clock/ambient don't shift when you
                toggle Live↔Playback (David 2026-08-07). */}
            {canJump && (
              <button
                type="button"
                className={`mobile-topbar__crumb mobile-topbar__crumb--sys${onJump ? '' : ' mobile-topbar__crumb--reserved'}`}
                onClick={onJump || undefined}
                disabled={!onJump}
                aria-hidden={onJump ? undefined : true}
                tabIndex={onJump ? undefined : -1}
                title="Switch system (Jump)"
                aria-label="Switch system (Jump)"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" /></svg>
                <span className="mobile-topbar__crumb-lab">{sName || 'NVR'}</span>
                <span className="mobile-topbar__crumb-car">&#9662;</span>
              </button>
            )}
            {canJump && onViews && <span className={`mobile-topbar__crumb-sep${onJump ? '' : ' mobile-topbar__crumb-sep--reserved'}`}>/</span>}
            {onViews && (
              <button type="button" className="mobile-topbar__crumb mobile-topbar__crumb--view" onClick={onViews} title="Change or edit view" aria-label="Change or edit view">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" /></svg>
                <span className="mobile-topbar__crumb-lab">{viewLabel || 'Views'}</span>
                {viewDirty && <span className="mobile-topbar__crumb-dot" title="Unsaved changes" />}
                <span className="mobile-topbar__crumb-car">&#9662;</span>
              </button>
            )}
          </div>
        )}
        { (nvrTime || nvrDate) &&
          <span className="mobile-topbar__clock">
            <span className="mobile-topbar__clock-dt">
              { nvrDate && <span className="mobile-topbar__date">{nvrDate}</span> }
              { nvrTime && <span className="mobile-topbar__time">{nvrTime}</span> }
            </span>
            {/* Ambient status under the clock (tablet+, David 2026-08-07): serial · days rec · users online.
                CSS-gated under .mobile-topbar--crumbs, so mobile is untouched. */}
            {(onJump || onViews) && (showSerial || daysRec != null || onlineCount != null) && (
              <span className="mobile-topbar__ambient">
                {showSerial && <span className="mobile-topbar__ambient-item">{bSerial}</span>}
                {daysRec != null && <span className="mobile-topbar__ambient-item">{daysRec} {daysRec === 1 ? 'Day' : 'Days'}</span>}
                {onlineCount != null && <span className="mobile-topbar__ambient-item">{onlineCount} online</span>}
              </span>
            )}
          </span> }
        {/* Tablet+ right cluster (#86bb7phhh) — system-details ⓘ + account avatar, mirroring the desktop
            header's right side (minus the Live/Playback toggle + Setup, which the bottom nav owns). Only
            rendered with the crumbs (tablet Live); mobile keeps its meta row below, untouched. */}
        {/* ⓘ system-details sits with the clock (David 2026-08-07) — close to the time, away from the
            account avatar so the two aren't mis-tapped. */}
        {(onJump || onViews) && onInfoClick && (
          <button type="button" className="mobile-topbar__act-btn mobile-topbar__info-inline" onClick={onInfoClick} aria-label="System details" title="System details">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" /></svg>
          </button>
        )}
        {(onJump || onViews) && (
          <div className="mobile-topbar__actions">
            <button type="button" className="mobile-topbar__acct" onClick={() => setAcctOpen(true)} aria-haspopup="dialog" aria-expanded={acctOpen} title={userName || 'Account'}>
              <span className="mobile-topbar__acct-av">{acctInitial}</span>
              <span className="mobile-topbar__acct-car">&#9662;</span>
            </button>
          </div>
        )}
      </div>
      <div className="mobile-topbar__meta">
        <span className="mobile-topbar__badges">
          <span className="mobile-topbar__badge">{badge}</span>
          { showSerial && <span className="mobile-topbar__serial">{bSerial}</span> }
          {/* Days recording — beside the serial (the system-status cluster), #86bb5dx62. */}
          { daysRec != null && <span className="mobile-topbar__days">{daysRec} {daysRec === 1 ? 'Day' : 'Days'}</span> }
        </span>
        { userName && <span className="mobile-topbar__user" title={userName}>{userName}</span> }
      </div>
      {/* Account = a BOTTOM SHEET (shared Sheet) so it matches Views/Jump/ⓘ on tablet (David #86bb7phhh).
          hidePlayback + hideSetup: the bottom nav already owns those, so the sheet is just the identity +
          Full Viewer (dview) + Logout. */}
      {acctOpen && (
        <Sheet
          className="live-sheet mobile-topbar__acct-sheet"
          scrimClassName="live-sheet-scrim"
          title={`${userName || 'Account'}${productTag}`}
          ariaLabel="Account"
          onClose={() => setAcctOpen(false)}
        >
          <ControlBoxView hidePlayback hideSetup />
        </Sheet>
      )}
    </div>
  );
};

export default MobileTopBar;
